/**
 * File: AppLayout.tsx
 * Path: apps/admin/src/modules/layout/components/AppLayout.tsx
 * Description: Master layout with anchored heights utilizing tokenized spacing metrics.
 */

import { MotionConfig, motion } from "framer-motion";
import React from "react";
import { Outlet } from "react-router-dom";
import { useAppLayoutController } from "@/modules/layout/hooks/useAppLayoutController";
import { AppSidebar } from "./AppSidebar";
import { MobileNav } from "./MobileNav";
import { TimerProvider } from "@/shared/components/TimerProvider";
import { HelpDialog } from "@/modules/layout/components/HelpDialog";
import { TourOverlay } from "@/modules/layout/components/TourOverlay";
import { NewVersionBar } from "@/modules/layout/components/NewVersionBar";
import { NotificationsBridge } from "@/modules/layout/components/NotificationsBridge";

export const AppLayout: React.FC = () => {
  const { pathname, mainRef } = useAppLayoutController();

  return (
    // 1. Root: Locked to viewport height using system variables
    // MotionConfig: every framer animation in the console honours the OS
    // "reduce motion" setting.
    <MotionConfig reducedMotion="user">
      <TimerProvider>
      <div className="flex h-screen w-full bg-sidebar text-foreground overflow-hidden font-sans">
        {/* Desktop/Tablet Sidebar */}
        <AppSidebar />

        {/* 2. Main Content Wrapper: 
             Utilizes 'sm' (768px Tablet Floor) with micro-stack variables to frame the canvas. */}
        <div className="flex flex-1 flex-col h-full min-w-0 relative sm:py-stack-sm sm:pr-stack-sm sm:pl-0">
          {/* 3. The Canvas: 
               Removed 'sm:rounded-none' to adhere to sharp editorial design guidelines.
               Uses your 'section-md' (6rem / 96px) spacing token as a mobile navigation buffer. */}
          <NewVersionBar />
          <TourOverlay />
          <HelpDialog />
          <main
            ref={mainRef}
            className="relative flex-1 min-w-0 overflow-y-auto overflow-x-hidden bg-background isolate sm:border sm:border-border/60 shadow-2xl ring-1 ring-background/20 pb-section-md sm:pb-0"
          >
            {/* 4. The Content: 
                 Replaced static numbers with tokenized responsive system gutters. */}
            {/* 5. Page transition: keyed on the path so every page change
                 fades in with a short rise. Enter-only, so navigation never
                 waits on the old page leaving. */}
            <motion.div
              key={pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="w-full min-h-full p-gutter-sm sm:p-gutter-md md:p-gutter-lg"
            >
              <Outlet />
            </motion.div>
          </main>
          {/* Dims the page (never the sidebar) while a dialog, menu or pop-up is open; see .admin-scrim in globals.css. */}
          <div className="admin-scrim" aria-hidden />
        </div>

        {/* Mobile Bottom Navigation */}
        <MobileNav />

        {/* Push token refresh, notification taps, app badge */}
        <NotificationsBridge />
      </div>
      </TimerProvider>
    </MotionConfig>
  );
};
